import React, { useEffect, useState } from "react";
import { Row, Col, Image, Typography, theme } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import ChatContainer from "./ChatContainer";
import partnerImg from "../../../assets/fileicons/Partners.svg";
import internalImg from "../../../assets/fileicons/internal.svg";
import dayjs from "dayjs";
import { getChatSessionsApi } from "../../../services/chat.services";
import EmptyChat from "./EmptyChat";
import useIsMobile from "../../../hooks/useIsMobile";
import styles from "./ChatTabs.module.css";
import "./chatMobile.css";

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = dayjs(dateString);
  const now = dayjs();
  const diffMins = now.diff(date, "minute");
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}min ago`;
  if (diffMins < 1440) return date.format("h:mm A");
  return date.format("DD MMM");
};

const ChatTabs = ({ patientData }) => {
  const url = new URL(window?.location?.href);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));

  const [activeChat, setActiveChat] = useState(params?.chat);
  const [hoveredChat, setHoveredChat] = useState(null);
  const [chatSession, setChatSession] = useState([]);

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { token: antdToken } = theme.useToken();

  const colorPrimary = antdToken.colorPrimary;

  const handleSelect = (key) => {
    setActiveChat(key);
    url?.searchParams?.set("chat", key);
    navigate(`${document?.location.pathname}?${url.searchParams.toString()}`, {
      replace: true,
    });
    window.requestAnimationFrame(() => {
      const panel = document.querySelector(".chat-container");
      if (panel) {
        panel.scrollTop = 0;
      }
    });
  };

  useEffect(() => {
    setActiveChat(params?.chat);
  }, [params?.chat]);

  useEffect(() => {
    if (isMobile && !params?.chat) {
      handleSelect("partner");
    }
  }, [isMobile, params?.chat]);

  useEffect(() => {
    getChatSessionsApi(params?.patient)
      .then((res) => {
        setChatSession(res?.data?.data || []);
      })
      .catch((err) => console.log(err));
  }, [params?.patient]);

  const getSessionByType = (type) => {
    return chatSession?.find((s) => s.session_type === type);
  };

  const BASE_OPTIONS = [
    {
      key: "partner",
      label: "Partners chat",
      mobileLabel: "Partners",
      icon: partnerImg,
      alt: "partner",
    },
    {
      key: "internal",
      label: "Internal Chat",
      mobileLabel: "Internal",
      icon: internalImg,
      alt: "internal",
    },
  ];

  const effectiveChat = isMobile ? activeChat || "partner" : activeChat;

  const renderChatContainer = () => {
    if (effectiveChat === "internal") {
      return (
        <ChatContainer
          key="internal"
          patientData={patientData}
          mode="internal"
          isMobile={isMobile}
        />
      );
    }
    if (effectiveChat === "partner") {
      return (
        <ChatContainer
          key="partner"
          patientData={patientData}
          mode="partner"
          isMobile={isMobile}
        />
      );
    }
    return <EmptyChat />;
  };

  return (
    <div className={`${styles.root} chat-tabs-root`}>
      {isMobile && (
        <div className={styles.mobileToggle} role="tablist" aria-label="Chat type">
          <div className={styles.mobileToggleTrack}>
            {BASE_OPTIONS.map(({ key, mobileLabel, icon, alt }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={effectiveChat === key}
                className={`${styles.mobileToggleBtn} ${
                  effectiveChat === key ? styles.mobileToggleBtnActive : ""
                }`}
                onClick={() => handleSelect(key)}
              >
                <span
                  className={styles.mobileToggleIcon}
                  style={{ backgroundColor: colorPrimary }}
                >
                  <Image src={icon} alt={alt} preview={false} width={14} />
                </span>
                <span className={styles.mobileToggleLabel}>{mobileLabel}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Row className={styles.row}>
        {!isMobile && (
        <Col className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Typography.Title level={4} className={styles.sidebarTitle}>
              Chat
            </Typography.Title>
          </div>

          {BASE_OPTIONS.map(({ key, label, icon, alt }) => {
            const isActive = activeChat === key;
            const isHovered = hoveredChat === key;

            const session = getSessionByType(key);

            const handle = session?.sent_by?.display_name || "";
            const preview = session?.latest_message || "";
            const time = formatTime(session?.last_message_at);

            return (
              <div
                key={key}
                onClick={() => handleSelect(key)}
                onMouseEnter={() => setHoveredChat(key)}
                onMouseLeave={() => setHoveredChat(null)}
                className={`${styles.chatItem} ${
                  isActive
                    ? styles.chatItemActive
                    : isHovered
                      ? styles.chatItemHovered
                      : styles.chatItemDefault
                }`}
              >
                {isActive && (
                  <span
                    className={styles.activeDot}
                    style={{ backgroundColor: colorPrimary }}
                  />
                )}

                <div className={styles.chatItemContent}>
                  <div
                    className={styles.chatIconWrap}
                    style={{ backgroundColor: colorPrimary }}
                  >
                    <Image src={icon} alt={alt} preview={false} />
                  </div>

                  <div className={styles.chatItemBody}>
                    <div className={styles.chatItemTopRow}>
                      <Typography.Text strong className={styles.chatItemLabel}>
                        {label}
                      </Typography.Text>

                      <Typography.Text className={styles.chatItemTime}>
                        {time}
                      </Typography.Text>
                    </div>

                    <Typography.Text className={styles.chatItemHandle}>
                      {handle ? `@ ${handle}` : ""}
                    </Typography.Text>

                    <Typography.Text className={styles.chatItemPreview}>
                      {preview}
                    </Typography.Text>
                  </div>
                </div>
              </div>
            );
          })}
        </Col>
        )}

        <Col flex={1} className={styles.chatArea}>
          {renderChatContainer()}
        </Col>
      </Row>
    </div>
  );
};

export default ChatTabs;
